import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp, TrendingDown, Users, Target, MapPin, AlertTriangle,
  CheckCircle, Activity, BarChart3, Calendar
} from 'lucide-react';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const DistrictDetailModal = ({ district, isOpen, onClose, reportingPeriod }) => {
  const [districtData, setDistrictData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (isOpen && district) {
      fetchDistrictDetails();
    }
  }, [isOpen, district, reportingPeriod]);

  const fetchDistrictDetails = async () => {
    if (!district?.district) return;
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/api/dashboards/avt-v2/district-details/${district.district}`,
        { params: { reporting_period: reportingPeriod } }
      );
      setDistrictData(response.data);
    } catch (error) {
      console.error('Error fetching district details:', error);
      setDistrictData(null);
    } finally {
      setLoading(false);
    }
  };

  const getPerformanceColor = (pct) => {
    if (pct >= 85) return 'text-green-600';
    if (pct >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceBadge = (performance) => {
    const colors = {
      'Excellent': 'bg-green-100 text-green-800',
      'Good': 'bg-yellow-100 text-yellow-800',
      'Needs Improvement': 'bg-red-100 text-red-800'
    };
    return colors[performance] || 'bg-gray-100 text-gray-800';
  };

  const COLORS = ['#10b981', '#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6'];

  if (!isOpen || !district) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <MapPin className="w-6 h-6 text-blue-600" />
            {district.district} - District Details
          </DialogTitle>
          <DialogDescription>
            Comprehensive performance analysis and statistics
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading district details...</span>
          </div>
        ) : districtData ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="performance">Performance</TabsTrigger>
              <TabsTrigger value="trends">Trends</TabsTrigger>
              <TabsTrigger value="comparison">Comparison</TabsTrigger>
              <TabsTrigger value="insights">Insights & Actions</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-4 mt-4">
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Total AWCs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{districtData.summary.total_awc.toLocaleString()}</div>
                    <p className="text-xs text-gray-500 mt-1">Anganwadi Centers</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">AWC Visit Coverage</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className={`text-2xl font-bold ${getPerformanceColor(districtData.summary.awc_visit_pct)}`}>
                      {districtData.summary.awc_visit_pct}%
                    </div>
                    <Badge className={`mt-1 ${getPerformanceBadge(districtData.summary.overall_performance)}`}>
                      {districtData.summary.overall_performance}
                    </Badge>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Reporting Period</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-lg font-semibold">{districtData.reporting_period}</div>
                    <p className="text-xs text-gray-500 mt-1">Current Period</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Performance Rating</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Badge className={`text-sm ${getPerformanceBadge(districtData.summary.overall_performance)}`}>
                      {districtData.summary.overall_performance}
                    </Badge>
                  </CardContent>
                </Card>
              </div>

              {/* AWC Status Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    AWC Status Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={districtData.awc_breakdown}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {districtData.awc_breakdown.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-3">
                      {districtData.awc_breakdown.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: item.color }}
                            />
                            <span className="font-medium">{item.name}</span>
                          </div>
                          <span className="text-lg font-bold">{item.value.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Performance Tab */}
            <TabsContent value="performance" className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Role Performance */}
                <Card>
                  <CardHeader>
                    <CardTitle>Role Performance Metrics</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(districtData.role_performance).map(([role, data]) => (
                        <div key={role} className="p-4 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold capitalize">{role === 'awc' ? 'AWC' : role.toUpperCase()}</h4>
                            <Badge className={getPerformanceBadge(data.performance)}>
                              {data.performance}
                            </Badge>
                          </div>
                          <div className="space-y-2">
                            {role === 'awc' ? (
                              <>
                                <div className="flex justify-between text-sm">
                                  <span>Total AWCs:</span>
                                  <span className="font-medium">{data.total}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span>Target:</span>
                                  <span className="font-medium">{data.target}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span>Visited:</span>
                                  <span className="font-medium text-green-600">{data.visited}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span>Not Visited:</span>
                                  <span className="font-medium text-red-600">{data.not_visited}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span>Not Opened:</span>
                                  <span className="font-medium text-yellow-600">{data.not_opened}</span>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="flex justify-between text-sm">
                                  <span>Target:</span>
                                  <span className="font-medium">{data.target}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span>Visited:</span>
                                  <span className="font-medium text-green-600">{data.visited}</span>
                                </div>
                              </>
                            )}
                            <div className="flex justify-between text-sm font-bold pt-2 border-t">
                              <span>Visit Coverage:</span>
                              <span className={getPerformanceColor(data.visit_pct)}>
                                {data.visit_pct}%
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Performance Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle>Visit Coverage by Role</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={[
                        { name: 'PO', value: districtData.role_performance.po.visit_pct },
                        { name: 'CDPO', value: districtData.role_performance.cdpo.visit_pct },
                        { name: 'Supervisor', value: districtData.role_performance.supervisor.visit_pct },
                        { name: 'AWC', value: districtData.role_performance.awc.visit_pct }
                      ]}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="value" fill="#3b82f6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Trends Tab */}
            <TabsContent value="trends" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Performance Trends Over Time
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {districtData.time_series.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height={400}>
                        <LineChart data={districtData.time_series}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="period" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="awc_visit_pct"
                            stroke="#10b981"
                            strokeWidth={2}
                            name="AWC Visit %"
                          />
                          <Line
                            type="monotone"
                            dataKey="po_visit_pct"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            name="PO Visit %"
                          />
                          <Line
                            type="monotone"
                            dataKey="cdpo_visit_pct"
                            stroke="#8b5cf6"
                            strokeWidth={2}
                            name="CDPO Visit %"
                          />
                          <Line
                            type="monotone"
                            dataKey="supervisor_visit_pct"
                            stroke="#f59e0b"
                            strokeWidth={2}
                            name="Supervisor Visit %"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                      {districtData.trends && Object.keys(districtData.trends).length > 0 && (
                        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                          {Object.entries(districtData.trends).map(([key, value]) => (
                            <div key={key} className="p-3 bg-gray-50 rounded-lg">
                              <div className="text-sm text-gray-600 capitalize">
                                {key.replace(/_/g, ' ').replace('trend', '')}
                              </div>
                              <div className={`text-lg font-bold flex items-center gap-1 ${
                                value >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {value >= 0 ? (
                                  <TrendingUp className="w-4 h-4" />
                                ) : (
                                  <TrendingDown className="w-4 h-4" />
                                )}
                                {value > 0 ? '+' : ''}{value}%
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No trend data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Comparison Tab */}
            <TabsContent value="comparison" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Comparison with State Average
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(districtData.comparison_with_state).map(([key, data]) => (
                      <div key={key} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold capitalize">
                            {key.replace(/_/g, ' ').replace('visit', 'Visit Coverage')}
                          </h4>
                          <Badge className={
                            data.difference >= 0
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }>
                            {data.difference >= 0 ? '+' : ''}{data.difference}% vs State
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="text-sm text-gray-600">District</div>
                            <div className="text-2xl font-bold">{data.district}%</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">State Average</div>
                            <div className="text-2xl font-bold text-gray-500">{data.state_avg}%</div>
                          </div>
                        </div>
                        <div className="mt-3">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${Math.min(data.district, 100)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Insights & Actions Tab */}
            <TabsContent value="insights" className="space-y-4 mt-4">
              {districtData.improvement_analysis ? (
                <>
                  {/* Insights */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                        Key Insights
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="list-disc list-inside space-y-2">
                        {districtData.improvement_analysis.insights.map((insight, i) => (
                          <li key={i} className="text-sm text-gray-700">{insight}</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  {/* Recommendations */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-blue-500" />
                        Recommendations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="list-disc list-inside space-y-2">
                        {districtData.improvement_analysis.recommendations.map((rec, i) => (
                          <li key={i} className="text-sm text-gray-700">{rec}</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  {/* Action Items */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        Action Items
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {districtData.improvement_analysis.action_items.map((action, i) => (
                          <div key={i} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span 
                                  className={`px-2 py-1 text-xs font-medium rounded ${
                                    action.priority === 'High' ? 'bg-red-100 text-red-700' :
                                    action.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-blue-100 text-blue-700'
                                  }`}
                                >
                                  {action.priority} Priority
                                </span>
                                <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700">
                                  {action.role}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500">{action.timeline}</span>
                            </div>
                            <h6 className="text-sm font-semibold text-gray-900 mb-1">{action.action}</h6>
                            <p className="text-xs text-gray-600 mb-2">{action.description}</p>
                            <p className="text-xs text-gray-500">
                              <span className="font-medium">Responsible:</span> {action.responsible}
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Performance Summary */}
                  {districtData.improvement_analysis.performance_summary && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <BarChart3 className="w-5 h-5" />
                          Performance Summary
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <div className="text-xs text-gray-600">Overall Average</div>
                            <div className="text-lg font-bold">{districtData.improvement_analysis.performance_summary.overall_avg}%</div>
                          </div>
                          <div className="p-3 bg-red-50 rounded-lg">
                            <div className="text-xs text-gray-600">Weakest Role</div>
                            <div className="text-lg font-bold text-red-600">
                              {districtData.improvement_analysis.performance_summary.weakest_role}
                            </div>
                            <div className="text-xs text-gray-500">
                              {districtData.improvement_analysis.performance_summary.weakest_role_pct}%
                            </div>
                          </div>
                          <div className="p-3 bg-green-50 rounded-lg">
                            <div className="text-xs text-gray-600">Strongest Role</div>
                            <div className="text-lg font-bold text-green-600">
                              {districtData.improvement_analysis.performance_summary.strongest_role}
                            </div>
                            <div className="text-xs text-gray-500">
                              {districtData.improvement_analysis.performance_summary.strongest_role_pct}%
                            </div>
                          </div>
                          <div className="p-3 bg-orange-50 rounded-lg">
                            <div className="text-xs text-gray-600">Total Gap</div>
                            <div className="text-lg font-bold text-orange-600">
                              {districtData.improvement_analysis.performance_summary.total_gap}
                            </div>
                            <div className="text-xs text-gray-500">visits</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Card>
                  <CardContent className="py-8 text-center text-gray-500">
                    <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
                    <p>No improvement analysis available for this district.</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
            <p>Unable to load district details. Please try again.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DistrictDetailModal;

